import React from 'react';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, profileService } from '../services/apiService';
import TokenManager from '../services/tokenManager';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [globalError, setGlobalError] = useState("");
  const navigate = useNavigate();

  // Function to refresh profile from backend
  const refreshProfile = useCallback(async () => {
    const token = TokenManager.getAccessToken();
    if (!token) return null;
    
    try {
      console.log('Refreshing profile from backend...');
      const res = await profileService.getProfile();
      if (res && res.success && res.data) {
        // res.data may be { employee, permissions } or the employee object directly depending on endpoint
        const emp = res.data.employee ? res.data.employee : res.data;
        const permissions = res.data.permissions || [];

        const userData = {
          employee_id: emp.employee_id,
          employee_code: emp.employee_code,
          first_name: emp.first_name,
          last_name: emp.last_name,
          email: emp.email_id,
          role: emp.role,
          profile_photo: emp.profile_photo,
          department_name: emp.department_name,
          designation: emp.Designation,
          reporting_manager: emp.reporting_manager,
          permissions: permissions
        };

        // Persist userInfo in the same storage where the token currently exists
        try {
          const useSession = !!sessionStorage.getItem('authToken');
          if (useSession) {
            sessionStorage.setItem('userInfo', JSON.stringify(userData));
            sessionStorage.setItem('employee_code', emp.employee_code || '');
          } else {
            localStorage.setItem('userInfo', JSON.stringify(userData));
            localStorage.setItem('employee_code', emp.employee_code || '');
          }
        } catch (err) {
          console.warn('Could not persist refreshed userInfo', err);
        }

        // Force cache bust for profile image
        localStorage.setItem('profileImageVersion', Date.now().toString());
        
        setUser(userData);
        setIsAuthenticated(true);
        
        // Dispatch event for components to update
        window.dispatchEvent(new CustomEvent('profileUpdated', { 
          detail: { profile_photo: emp.profile_photo } 
        }));
        
        return userData;
      }
    } catch (err) {
      console.warn('Could not refresh profile:', err?.message || err);
      return null;
    }
  }, []);

  // Check if user is already logged in (on component mount)
  useEffect(() => {
    const checkAuth = async () => {
      const employeeCode = localStorage.getItem('employee_code') || sessionStorage.getItem('employee_code');
      const userInfo = localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo');
      const authToken = TokenManager.getAccessToken();

      if (employeeCode && userInfo && authToken) {
        try {
          // Set initial user from localStorage
          const parsedUser = JSON.parse(userInfo);
          setUser(parsedUser);
          setIsAuthenticated(true);
          
          // Then refresh from backend to get latest data
          await refreshProfile();
        } catch (err) {
          console.error('Error parsing user info:', err);
          localStorage.removeItem('userInfo');
          localStorage.removeItem('employee_code');
          localStorage.removeItem('authToken');
          sessionStorage.removeItem('userInfo');
          sessionStorage.removeItem('employee_code');
          sessionStorage.removeItem('authToken');
        }
      }
      setLoading(false);
    };
    checkAuth();

    // Listen for token expiry event (JWT expiration)
    const handleTokenExpired = () => {
      const token = TokenManager.getAccessToken();
      const userInfo = localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo');
      
      if (token && userInfo) {
        logout('Session expired. Please login again.');
      } else {
        TokenManager.clearTokens();
      }
    };

    // Listen for token revocation event (explicit logout from backend)
    const handleTokenRevoked = (event) => {
      const reason = event.detail?.reason || 'Your token has been revoked';
      logout(reason);
    };

    window.addEventListener('tokenExpired', handleTokenExpired);
    window.addEventListener('tokenRevoked', handleTokenRevoked);

    return () => {
      window.removeEventListener('tokenExpired', handleTokenExpired);
      window.removeEventListener('tokenRevoked', handleTokenRevoked);
    };
  }, [refreshProfile]);

  // Listen for profile updates from other parts of the app (e.g. Profile page)
  useEffect(() => {
    const onProfileUpdated = (e) => {
      try {
        const stored = sessionStorage.getItem('userInfo') || localStorage.getItem('userInfo');
        if (stored) {
          const ui = JSON.parse(stored);
          setUser(ui);
          setIsAuthenticated(true);
        } else if (e?.detail?.profile_photo) {
          setUser(prev => prev ? ({ ...prev, profile_photo: e.detail.profile_photo }) : prev);
        }

        // Force re-render by updating cache bust
        localStorage.setItem('profileImageVersion', Date.now().toString());
      } catch (err) {
        console.warn('profileUpdated handler failed', err);
      }
    };

    window.addEventListener('profileUpdated', onProfileUpdated);
    return () => window.removeEventListener('profileUpdated', onProfileUpdated);
  }, []);

  // Step 1: verify username/password. Employees are signed in immediately;
  // other roles get an OTP emailed and the backend returns
  // { requiresOtp: true, email_id } instead of tokens - verifyLoginOtp then
  // finishes the sign-in.
  // remember: boolean - whether to persist tokens across browser sessions
  const login = async (username, password, remember = false) => {
    try {
      setError(null);
      setLoading(true);
      const response = await authService.login(username, password);

      if (response.data.success) {
        const data = response.data.data || {};
        if (data.requiresOtp) {
          setError(null);
          return { success: true, requiresOtp: true, email_id: data.email_id };
        }
        // No OTP required (employee accounts): the backend issued tokens directly.
        return await completeLogin(response.data, remember);
      }

      const errorMsg = response.data.message || 'Login failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Login failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // Shared by verifyLoginOtp (and login's defensive fallback): stores tokens
  // and user info from a successful-login response body.
  const completeLogin = async (responseData, remember) => {
    const employee = responseData.data?.employee || {};
    const tokens = responseData.data?.tokens || {};
    const permissions = responseData.data?.permissions || [];

    const userData = {
      employee_id: employee.employee_id,
      employee_code: employee.employee_code,
      first_name: employee.first_name,
      last_name: employee.last_name,
      email: employee.email_id,
      role: employee.role,
      profile_photo: employee.profile_photo,
      department_name: employee.department_name,
      designation: employee.Designation,
      reporting_manager: employee.reporting_manager,
      permissions: permissions,
    };

    // Store tokens using TokenManager according to remember flag
    TokenManager.storeTokens(tokens, responseData.data?.session_id || null, remember);
    // Store user info and employee_code in same storage as tokens
    try {
      if (remember) {
        localStorage.setItem('userInfo', JSON.stringify(userData));
        localStorage.setItem('employee_code', employee.employee_code || '');
      } else {
        sessionStorage.setItem('userInfo', JSON.stringify(userData));
        sessionStorage.setItem('employee_code', employee.employee_code || '');
      }
    } catch (err) {
      console.warn('Could not persist userInfo/employee_code', err);
    }

    // Force cache bust for profile image
    localStorage.setItem('profileImageVersion', Date.now().toString());

    setUser(userData);
    setIsAuthenticated(true);

    // IMPORTANT: Refresh profile from backend to ensure we have the latest data
    await refreshProfile();

    setError(null);
    return { success: true };
  };

  // Step 2 of 2: verify the OTP emailed during login(), then complete sign-in.
  // remember: boolean - whether to persist tokens across browser sessions
  const verifyLoginOtp = async (email_id, otp, remember = false) => {
    try {
      setError(null);
      setLoading(true);
      const response = await authService.verifyLoginOtp(email_id, otp);

      if (response.data.success) {
        return await completeLogin(response.data, remember);
      }

      const errorMsg = response.data.message || 'OTP verification failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'OTP verification failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  const logout = async (message) => {
    try {
      setLoading(true);
      // Call backend logout to blacklist token
      await authService.logout();
    } catch (err) {
      console.error('Error calling logout API:', err);
    } finally {
      // Always clear local state and redirect, even if API call fails
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      
      // Clear all auth-related data (both storages)
      TokenManager.clearTokens();
      localStorage.removeItem('profileImageVersion');
      
      if (message) {
        setGlobalError(message);
        // Clear error after 5 seconds
        setTimeout(() => setGlobalError(''), 5000);
      }

      navigate('/login');
      setLoading(false);
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    error,
    login,
    verifyLoginOtp,
    logout,
    refreshProfile, // Expose this so components can manually refresh
    globalError,
    setGlobalError,
  };

  return (
    <AuthContext.Provider value={value}>
      {globalError && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          background: '#f44336',
          color: '#fff',
          padding: '10px',
          zIndex: 9999,
          textAlign: 'center',
          fontWeight: 'bold'
        }}>
          {globalError}
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};